import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { SalesTableComparativeStats } from './SalesTableComparativeStats';

interface SalesPieChartProps {
  sales: any[];
  productById: Record<string, any>;
  periodLabel?: string;
  customColor?: string;
  title?: string;
  showComparativeStats?: boolean;
}

export const SalesPieChart: React.FC<SalesPieChartProps> = ({ 
  sales, 
  productById,
  periodLabel = '',
  customColor,
  title,
  showComparativeStats = false
}) => {

  const pieChartData = useMemo(() => {
    const productCounts = sales.reduce((acc, sale) => {
      const productId = sale.product_id;
      const quantity = Number(sale.quantity_sold) || 0;
      acc[productId] = (acc[productId] || 0) + quantity;
      return acc;
    }, {} as Record<string, number>);

    const productData = Object.entries(productCounts)
      .map(([productId, count]) => ({
        name: productById[productId]?.name || 'Produit inconnu',
        value: count,
        product: productById[productId]
      }))
      .filter(item => item.product)
      .sort((a, b) => Number(b.value) - Number(a.value))
      .slice(0, 8);

    const otherCount = Object.entries(productCounts)
      .slice(8)
      .reduce((sum, [, count]) => sum + Number(count), 0);

    if (otherCount > 0) {
      productData.push({
        name: 'Autres',
        value: otherCount,
        product: null
      });
    }

    return productData;
  }, [sales, productById]);

  const pieColors = useMemo(() => {
    if (customColor) {
      // Génère des variations de la couleur personnalisée
      return [
        customColor,
        customColor + '80', // Plus clair
        customColor + '60', // Encore plus clair
        customColor + 'CC', // Semi-transparent
        customColor + 'AA', // Plus transparent
        'hsl(221 83% 53%)',  // Bleu par défaut pour les autres
        'hsl(32 95% 60%)',   // Orange
        'hsl(271 91% 65%)',  // Violet
        'hsl(210 40% 70%)'   // Gris pour "Autres"
      ];
    }
    return [
      'hsl(220 91% 42%)',  // Bleu professionnel (primary)
      'hsl(142 76% 36%)',  // Vert
      'hsl(35 90% 55%)',   // Orange
      'hsl(0 75% 55%)',    // Rouge
      'hsl(271 91% 65%)',  // Violet
      'hsl(45 93% 47%)',   // Jaune
      'hsl(340 82% 52%)',  // Rose
      'hsl(200 98% 39%)',  // Cyan
      'hsl(20 90% 48%)',   // Orange brûlé
      'hsl(210 40% 60%)'   // Gris pour "Autres"
    ];
  }, [customColor]);

  return (
    <div className="space-y-6">
      <Card className="transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp 
              className="w-5 h-5" 
              style={customColor ? { color: customColor } : undefined}
            />
            <span style={customColor ? { color: customColor } : undefined}>
              {title || `Répartition des ventes ${periodLabel}`}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pieChartData.length > 0 ? (
            <div className="h-[500px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={{
                      stroke: customColor || "hsl(var(--muted-foreground))",
                      strokeWidth: 1
                    }}
                    label={({ name, value, percent }) => {
                      return `${name} (${(percent * 100).toFixed(0)}%)`;
                    }}
                    outerRadius={140}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="white"
                    strokeWidth={2}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: any, name: any) => [value, 'Articles vendus']}
                    labelFormatter={(label) => `Produit: ${label}`}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">Aucune donnée disponible</p>
              <p className="text-sm">Aucune vente trouvée pour cette période</p>
            </div>
          )}
        </CardContent>
      </Card>

      {showComparativeStats && (
        <SalesTableComparativeStats sales={sales} customColor={customColor} />
      )}
    </div>
  );
};